import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Page-load and scroll-into-view reveal, applied site-wide without touching each section:
 * - serif headlines and statements rise out of a mask,
 * - labels, body copy, buttons and list rows fade up,
 * - images wipe open from the bottom.
 * Elements already on screen reveal in a short stagger on load; the rest as they scroll in.
 * Uses the CSS `translate` property and clip-path, so it never fights framer-motion transforms.
 */

const TEXT = 'h1, h2:not(.sr-only), p.font-serif';
const FADE = 'section p:not(.font-serif), section h3, section dl, section a.rounded-control, section ul > li, section ol > li';
const MEDIA = 'section img';
// Marquees and horizontal carousels are skipped so items don't pop in as they slide sideways
const SKIP = 'nav, footer, header, [role="dialog"], [data-no-reveal], [aria-roledescription="slide"], .sr-only';
const STAGGER = 80; // ms between items revealed together
const MAX_STAGGER_ITEMS = 8;
export function RevealOnScroll() {
  const {
    pathname
  } = useLocation();
  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const root = document.querySelector('main');
    if (!root) return;
    const picked = new Map<HTMLElement, string>();
    const add = (selector: string, kind: string) => {
      root.querySelectorAll<HTMLElement>(selector).forEach(el => {
        if (picked.has(el) || el.closest(SKIP)) return;
        // Don't reveal an element whose ancestor is already revealing — avoids double motion
        for (const parent of picked.keys()) if (parent.contains(el)) return;
        picked.set(el, kind);
      });
    };
    add(TEXT, 'rv-text');
    add(MEDIA, 'rv-media');
    add(FADE, 'rv-fade');
    const els = [...picked.keys()];
    els.forEach(el => el.classList.add('rv', picked.get(el) as string));
    const timers: number[] = [];
    const io = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting).map(e => e.target as HTMLElement).sort((a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
      visible.forEach((el, i) => {
        io.unobserve(el);
        el.style.transitionDelay = `${Math.min(i, MAX_STAGGER_ITEMS) * STAGGER}ms`;
        el.classList.add('rv-in');
        // Clear the delay afterwards so hover transitions stay instant
        timers.push(window.setTimeout(() => {
          el.style.transitionDelay = '';
          el.classList.remove('rv', 'rv-text', 'rv-fade', 'rv-media', 'rv-in');
        }, 1600 + i * STAGGER));
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01
    });

    // Wait one frame so the hidden state paints before anything reveals (gives the load effect)
    const raf = requestAnimationFrame(() => els.forEach(el => io.observe(el)));
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      timers.forEach(clearTimeout);
      els.forEach(el => {
        el.style.transitionDelay = '';
        el.classList.remove('rv', 'rv-text', 'rv-fade', 'rv-media', 'rv-in');
      });
    };
  }, [pathname]);
  return null;
}